import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { RedirectCommand, Router } from '@angular/router';

import { combineLatest, filter, map, Observable, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  contractStatusConverter,
  getContracts,
  getContractsActionState,
  getPermissionsActionState,
  getTicketSettings,
  IActionState,
  IContract,
  ITicketSettings,
  LoadContracts,
  LoadPermissions,
} from '@resident-nx/shared';

import { ROUTE } from '../../models/navigation.model';

@Injectable({ providedIn: 'root' })
export class TicketingGuardService {
  private router = inject(Router);
  private store = inject(Store);
  private location = inject(Location);
  private path = '';

  constructor() {
    this.redirectPath(this.location.path())
      .pipe(takeUntilDestroyed())
      .subscribe(p => {
        this.path = p;
      });
  }

  setPath(path: string) {
    this.path = path;
  }

  TicketingGuard(): Observable<boolean | RedirectCommand> {
    return this.getContractAndPermissionInfo().pipe(
      map(([contracts, ticketSettings]) => {
        const allowContractlessTicketing =
          ticketSettings?.allowContractlessTicketCreationForDamages ||
          ticketSettings?.allowContractlessTicketCreationForOtherConcerns;

        const canActivate = contractStatusConverter.isAnyContractActiveOrEnded(contracts)
          ? true
          : allowContractlessTicketing;

        return canActivate ? true : new RedirectCommand(this.router.parseUrl('/dashboard'));
      })
    );
  }

  redirectPath(path: string): Observable<string> {
    return this.getContractAndPermissionInfo().pipe(
      map(([contracts, ticketSettings]) => {
        if (contracts.length >= 1) {
          return path.includes(ROUTE.REQUESTS) ? ROUTE.REQUESTS : ROUTE.DAMAGES;
        }
        if (ticketSettings?.allowContractlessTicketCreationForDamages) return ROUTE.DAMAGES;
        if (
          !ticketSettings?.allowContractlessTicketCreationForDamages &&
          ticketSettings?.allowContractlessTicketCreationForOtherConcerns
        )
          return ROUTE.REQUESTS;
        return ROUTE.DAMAGES;
      })
    );
  }

  DamageTicketingGuard(): Observable<boolean | RedirectCommand> {
    return this.getContractAndPermissionInfo().pipe(
      map(([contracts, ticketSettings]) => {
        const canActivate = contractStatusConverter.isAnyContractActiveOrEnded(contracts)
          ? true
          : ticketSettings?.allowContractlessTicketCreationForDamages;
        return canActivate ? true : new RedirectCommand(this.router.parseUrl('/tickets/requests'));
      })
    );
  }

  ConcernTicketingGuard(): Observable<boolean | RedirectCommand> {
    return this.getContractAndPermissionInfo().pipe(
      map(([contracts, ticketSettings]) => {
        const canActivate = contractStatusConverter.isAnyContractActiveOrEnded(contracts)
          ? true
          : ticketSettings?.allowContractlessTicketCreationForOtherConcerns;
        return canActivate ? true : new RedirectCommand(this.router.parseUrl('/tickets/damages'));
      })
    );
  }

  private getContractAndPermissionInfo(): Observable<[IContract[], ITicketSettings]> {
    return combineLatest([
      this.store.select(getContractsActionState),
      this.store.select(getPermissionsActionState),
    ]).pipe(
      tap(([contractsActionState, permissionsActionState]: [IActionState, IActionState]) => {
        if (!contractsActionState.pending && !contractsActionState.done) {
          this.store.dispatch(LoadContracts({}));
        }
        if (!permissionsActionState.pending && !permissionsActionState.done) {
          this.store.dispatch(LoadPermissions());
        }
      }),
      filter(
        ([contractsActionState, permissionsActionState]) =>
          contractsActionState.done && permissionsActionState.done
      ),
      switchMap(() =>
        combineLatest([this.store.select(getContracts), this.store.select(getTicketSettings)])
      )
    );
  }
}
